import { ButtonType, InputText } from "@constellation-academy/epic-ui";
import { Dialog } from "primereact/dialog";
import { useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { Button, ButtonVariant } from "@components/button";
import { useTranslation } from "@i18n/hooks";
import { type closeOrderAction_CloseOrderMutation } from "@relay/closeOrderAction_CloseOrderMutation.graphql";
import { type closeOrderAction_OrderFragment$key } from "@relay/closeOrderAction_OrderFragment.graphql";
import { CLOSE_ORDER_MUTATION, ORDER_FRAGMENT } from "./close-order-action.graphql";
import {
	dialogFooterClass,
	hintClass,
	reasonFieldClass,
	wrapperClass,
} from "./close-order-action.styles";
import { type CloseOrderActionProps } from "./close-order-action.types";

export const CloseOrderAction = ({ orderFragmentRef }: CloseOrderActionProps) => {
	const { t } = useTranslation("common");
	const order = useFragment<closeOrderAction_OrderFragment$key>(ORDER_FRAGMENT, orderFragmentRef);
	const [closeOrder, isClosing] =
		useMutation<closeOrderAction_CloseOrderMutation>(CLOSE_ORDER_MUTATION);
	const [isDialogVisible, setIsDialogVisible] = useState(false);
	const [reason, setReason] = useState("");

	const isAwaitingAccount = order.status === "PurchasedAwaitingAccount";
	const trimmedReason = reason.trim();

	const handleConfirmOnClick = () => {
		closeOrder({
			variables: { input: { orderId: order.id, reason: trimmedReason } },
			onCompleted: () => {
				setIsDialogVisible(false);
				setReason("");
				toast.success(t("order.orderClosed"));
			},
			onError: () => {
				toast.error(t("order.closeOrderFailed"));
			},
		});
	};

	return (
		<div className={wrapperClass}>
			<div className={hintClass}>{t("order.closeOrderHint")}</div>
			<Button
				variant={ButtonVariant.Error}
				type={ButtonType.Button}
				label={t("order.closeOrder")}
				disabled={!isAwaitingAccount || isClosing}
				onClick={() => setIsDialogVisible(true)}
			/>
			<Dialog
				header={t("order.closeOrder")}
				visible={isDialogVisible}
				onHide={() => setIsDialogVisible(false)}
				style={{ width: "32rem" }}
				footer={
					<div className={dialogFooterClass}>
						<Button
							variant={ButtonVariant.Outline}
							type={ButtonType.Button}
							label={t("actions.cancel")}
							disabled={isClosing}
							onClick={() => setIsDialogVisible(false)}
						/>
						<Button
							variant={ButtonVariant.Error}
							type={ButtonType.Button}
							label={t("order.closeOrder")}
							disabled={isClosing || trimmedReason.length === 0}
							onClick={handleConfirmOnClick}
						/>
					</div>
				}
			>
				<div className={reasonFieldClass}>
					<InputText
						label={t("order.closeOrderReason")}
						value={reason}
						onChange={(value) => setReason(value ?? "")}
					/>
				</div>
				<div>{t("order.closeOrderConfirm")}</div>
			</Dialog>
		</div>
	);
};
